import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useRouteContext, useRouter } from '@tanstack/react-router';
import { Button, Card, Divider, Flex, Form, Input, message, Modal, Tag } from 'antd';
import { HeartCrack } from 'lucide-react';
import { useEffect } from 'react';

import {
  getGetAccountQueryKey,
  useLeaveOrganizationMembership,
  useUpdateOrganizationMembershipProfile,
} from '../../../../api';
import { EntityAvatar } from '../../../../modules/avatar/components/entity-avatar';
import { SettingsLayoutContent } from '../../../../ui/components/layout';
import { Record } from '../../../../ui/components/record';
import { useUnsavedChangesGuard } from '../../../../ui/hooks';

const SettingsMembershipRoute = () => {
  const navigate = useNavigate();
  const router = useRouter();

  const queryClient = useQueryClient();

  const { account, selectedOrganization, selectedMembership } = useRouteContext({
    from: '/_authenticated/_app',
  });

  const isOwner = selectedMembership.role.name.toLowerCase() === 'owner';

  const [form] = Form.useForm();
  Form.useWatch([], form);
  useUnsavedChangesGuard(() => form.isFieldsTouched());

  useEffect(() => {
    form.resetFields();
  }, [selectedMembership, form]);

  const { mutate: updateProfile, isPending: isUpdating } = useUpdateOrganizationMembershipProfile({
    mutation: {
      onSuccess: () => {
        message.success('Saved');
        queryClient.resetQueries({ queryKey: getGetAccountQueryKey() });
        router.invalidate();
      },
      onError: () => {
        message.error('Failed to save');
      },
    },
  });

  const { mutateAsync: leaveMembershipAsync, isPending: isLeaving } =
    useLeaveOrganizationMembership({
      mutation: {
        onSuccess: () => {
          message.success('You have left the team.');
          queryClient.resetQueries({ queryKey: getGetAccountQueryKey() });
          navigate({ to: '/' });
        },
        onError: () => {
          message.error('Failed to leave team');
        },
      },
    });

  if (!selectedMembership) {
    return (
      <SettingsLayoutContent title="Membership" description="Your membership in this workspace.">
        <p>You are not a member of this organization.</p>
      </SettingsLayoutContent>
    );
  }

  return (
    <SettingsLayoutContent
      title="Membership"
      description="Your role and profile in this workspace."
    >
      <Form
        form={form}
        layout="vertical"
        validateTrigger="onSubmit"
        initialValues={{ jobTitle: selectedMembership.jobTitle ?? '' }}
        onFinish={(values) =>
          updateProfile({
            organizationId: selectedMembership.organization.id,
            organizationMembershipId: selectedMembership.id,
            data: { jobTitle: values.jobTitle.trim() || null },
          })
        }
      >
        <Card>
          <Record
            left={
              <EntityAvatar
                kind="user"
                id={account.id}
                index={account.avatarIndex}
                name={`${account.firstName} ${account.lastName}`}
                size={56}
              />
            }
            title={
              <Flex gap={8} align="center">
                {account.firstName} {account.lastName}
                <Tag variant="filled">{selectedMembership.role.name}</Tag>
              </Flex>
            }
            description={account.email}
            size="large"
          />

          <Divider dashed />

          <Form.Item name="jobTitle" label="Job Title">
            <Input size="large" placeholder="e.g. Sales Manager" />
          </Form.Item>

          <Form.Item shouldUpdate noStyle>
            {({ isFieldsTouched }) => {
              const isDirty = isFieldsTouched();
              return (
                <Flex justify={isDirty ? 'space-between' : 'flex-end'}>
                  {isDirty && (
                    <Button onClick={() => form.resetFields()} disabled={isUpdating}>
                      Reset
                    </Button>
                  )}
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={isUpdating}
                    disabled={!isDirty && !isUpdating}
                  >
                    Save
                  </Button>
                </Flex>
              );
            }}
          </Form.Item>
        </Card>

        {!isOwner && (
          <>
            <Divider dashed style={{ margin: '32px 0' }} />
            <Card>
              <Record
                left={<HeartCrack size={24} style={{ color: 'var(--ant-color-danger)' }} />}
                title="Leave the Team"
                description="Leave this workspace. You will lose access until you are invited again."
                right={
                  <Button
                    color="danger"
                    variant="filled"
                    onClick={() => {
                      Modal.confirm({
                        title: 'Leave the Team',
                        content: 'Are you sure you want to leave this workspace?',
                        okText: 'Leave',
                        cancelText: 'Cancel',
                        okButtonProps: { danger: true, loading: isLeaving },
                        onOk: () =>
                          leaveMembershipAsync({
                            organizationId: selectedOrganization.id,
                            organizationMembershipId: selectedMembership.id,
                          }),
                      });
                    }}
                  >
                    Leave
                  </Button>
                }
                size="large"
              />
            </Card>
          </>
        )}
      </Form>
    </SettingsLayoutContent>
  );
};

export const Route = createFileRoute('/_authenticated/_app/settings/membership')({
  component: SettingsMembershipRoute,
});
